import * as React from "react";

import {
  HoverActionToolbar,
  hoverActions,
} from "@/shared/components/RobotoMarkdown/HoverActionToolbar";
import { useJumpToTimestampAction } from "@/shared/components/RobotoMarkdown/timeFormat";
import { Timestamp } from "@/shared/components/RobotoMarkdown/Timestamp";
import { IngestionStatus } from "@/shared/domain/files";
import { useFile } from "@/shared/domain/files/hooks";
import { FilePlus } from "@/shared/foundation/icons";
import { usePublishToAiAgentThreadPubSubBus } from "@/shared/services/AiAgentThreadPubSubBus";
import {
  WORKSPACE_ACTION_TOPIC,
  WorkspaceActionPayload,
} from "@/shared/services/AiAgentThreadPubSubBus/models";
import { useViewingContext } from "@/shared/services/ViewingContextService";

import { HighlightedSpan } from "./HighlightedSpan";

interface VizFileLinkProps {
  fileId: string;
  time: bigint;
  text: string;
}

/**
 * A file link that carries a time, rendered while the visualizer is open. It
 * renders as a {@link Timestamp}, except when the file has ingested data the
 * workspace has not loaded: then its hover toolbar offers "Add <file> to the
 * visualizer", which loads the file and moves the playhead to the time, next
 * to "Jump to timestamp".
 *
 * The Timestamp covers every file with nothing to add: one the workspace
 * already shows, one that was never ingested (the AI links to one when an
 * event was recorded on it, such as a mission summary log), and one whose
 * record cannot be fetched. None of them navigates away from the workspace
 * the reader is in.
 */
export const VizFileLink: React.FC<VizFileLinkProps> = ({
  fileId,
  time,
  text,
}) => {
  const loaded =
    useViewingContext().visualizer?.dataSources.some(
      (source) => source.type === "file" && source.id === fileId,
    ) ?? false;
  const file = useFile(loaded ? undefined : fileId);
  const jumpAction = useJumpToTimestampAction(time);
  const publish = usePublishToAiAgentThreadPubSubBus<WorkspaceActionPayload>(
    WORKSPACE_ACTION_TOPIC,
  );

  // Plain text until the record arrives, so a file not fetched yet is not
  // shown as one with nothing to add.
  if (file.isLoading) {
    return <span>{text}</span>;
  }
  if (
    file.data === undefined ||
    file.data.ingestion_status === IngestionStatus.NotIngested
  ) {
    return <Timestamp ns={time}>{text}</Timestamp>;
  }

  return (
    <HoverActionToolbar
      actions={hoverActions(
        {
          key: "add-file",
          icon: <FilePlus />,
          tooltip: `Add ${file.data.relative_path} to the visualizer`,
          onClick: () => {
            publish({ type: "add_file", payload: { fileId, timestamp: time } });
          },
        },
        jumpAction,
      )}
    >
      <HighlightedSpan>{text}</HighlightedSpan>
    </HoverActionToolbar>
  );
};
